<template>
  <div
    :style="{
      top: marker.positionY + 'px',
      left: marker.positionX + 'px',
    }"
    :class="compact ? 'fixed-tooltip compact-tooltip absolute bg-white shadow-lg' : 'fixed-tooltip absolute bg-white p-2 text-sm shadow-lg'"
    @mousedown.stop.prevent="$emit('drag-start', $event)">

    <div class="tooltip-marker absolute"></div>
    <template v-if="compact">
      <div class="compact-header">
        <p class="compact-time">{{ compactDate }}</p>
        <div >
          <button v-if="(marker.values?.length || 0) > 0" class="icon-btn" title="Ocultar todas as séries" @click="$emit('remove', marker)">
            <IcDeleteClose class="h-3 w-3" />
          </button>
        </div>
      </div>
      <template v-if="marker.values?.length">
        <div class="compact-values">
          <p v-for="(item, idx) in marker.values" :key="`value-${idx}-${item.seriesName}`" class="compact-value-item">
            <span class="compact-series">{{ item.seriesName }}</span>
            <span class="compact-value">{{ item.value }}</span>
            <button class="icon-btn icon-btn-inline" title="Ocultar esta série" @click="$emit('remove-series', item.seriesName)">
              <IcCloseSm class="h-3 w-3" />
            </button>
          </p>
        </div>
      </template>
      <template v-else>
        <p class="compact-value-item">
          <span class="compact-value">{{ marker.y }}</span>
        </p>
      </template>
    </template>
    <template v-else>
      <p>
        <strong>{{ marker.seriesName }}</strong>
      </p>
      <p>{{ t('dashboard.body.chart.subtitles.date', { date: formatWithLocalTimezone(marker.x.getTime()) }) }}</p>
      <p>{{ t('dashboard.body.chart.subtitles.value', { value: marker.y }) }}</p>
    </template>
  </div>
</template>

<script setup lang="ts">
import IcCloseSm from '@/components/Icons/IcCloseSm.vue'
import IcDeleteClose from '@/components/Icons/IcDeleteClose.vue'
import { ILineChartMarker } from '@/interfaces/components/chats/chart-marker'
import { formatWithLocalTimezone } from '@/utils/date/date-time'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
const props = defineProps<{
  marker: ILineChartMarker
  compact?: boolean
}>()
defineEmits<{
  (e: 'remove-series', seriesName: string): void
  (e: 'reset-series'): void
  (e: 'clear-series'): void
  (e: 'remove', marker: ILineChartMarker): void
  (e: 'drag-start', event: MouseEvent): void
}>()

const { t } = useI18n()
const compact = computed(() => props.compact ?? false)
const compactDate = computed(() => {
  const d = new Date(props.marker.x)
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
})
</script>

<style scoped>
.fixed-tooltip {
  z-index: 8;
  pointer-events: auto;
  transform: translate(-50%, -100%);
  position: absolute;
  cursor: move;
}

.compact-tooltip {
  padding: 8px 10px;
  min-width: 160px;
  max-width: 260px;
  font-size: 11px;
  line-height: 1.3;
  border-radius: 0;
  border: 1px solid #e5e7eb;
  box-shadow:
    0 8px 24px rgba(15, 23, 42, 0.12),
    0 2px 6px rgba(15, 23, 42, 0.08);
}

.compact-close {
  right: 4px;
  top: 3px;
}

.compact-time {
  font-size: 11px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 6px;
  padding-right: 14px;
}

.compact-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}

.compact-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  padding-right: 12px;
}

.compact-values {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.compact-value-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0;
  padding: 2px 6px;
}

.compact-series {
  color: #475569;
  max-width: 90px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.compact-value {
  color: #0f172a;
  font-weight: 600;
  white-space: nowrap;
}

.icon-btn {
  border: 1px solid #cbd5e1;
  border-radius: 0;
  width: 18px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #475569;
  background: #ffffff;
}

.icon-btn:hover {
  background: #f1f5f9;
  color: #0f172a;
}

.icon-btn-inline {
  margin-left: auto;
}

button {
  pointer-events: auto;
}

.tooltip-marker {
  width: 9px;
  height: 9px;
  background-color: #2563eb;
  border-radius: 50%;
  top: 100%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  position: absolute;
  box-shadow: 0 0 0 2px #dbeafe;
}
</style>
